import { BaseCommand } from '../../../core/commands/ICommand';
import type { PositionStaffingIntent } from '../models/PositionStaffingIntent';

export interface PositionStaffingCommandAccess {
  readonly canWrite: boolean;
  readonly canUseScenarios: boolean;
}

export interface PositionStaffingCommandServerPort {
  readonly historyRecoveryId?: string;
  execute(): Promise<void>;
  undo(): Promise<void>;
  redo(): Promise<void>;
}

export interface PositionStaffingCommandParams {
  readonly intent: PositionStaffingIntent;
  readonly access: PositionStaffingCommandAccess;
  readonly serverPort: PositionStaffingCommandServerPort;
  /** External/reviewed operational changes are reversed by a new change, not local undo. */
  readonly skipHistory?: boolean;
}

/**
 * Undoable command boundary for durable Position Chart staffing changes.
 * The command never mutates Yjs; its port owns the protected server lifecycle.
 */
export class PositionStaffingCommand extends BaseCommand {
  get historyRecoveryId(): string | undefined { return this.params.serverPort.historyRecoveryId; }
  constructor(private readonly params: PositionStaffingCommandParams) {
    super(
      staffingCommandName(params.intent),
      staffingCommandDescription(params.intent),
      { skipHistory: params.skipHistory,
        ...(params.intent.kind === 'fill_role' ? { mutationDestination: 'protected_plan' as const } : {}) },
    );
  }

  canExecute(): boolean {
    return this.params.access.canWrite
      && (this.params.intent.mode === 'live' || this.params.access.canUseScenarios);
  }

  execute(): Promise<void> {
    if (!this.canExecute()) {
      throw new Error(this.params.intent.mode === 'scenario'
        ? 'Scenario staffing is not available for this workspace.'
        : 'Position staffing is not available for this workspace.');
    }
    return this.params.serverPort.execute();
  }

  undo(): Promise<void> {
    return this.params.serverPort.undo();
  }

  redo(): Promise<void> {
    return this.params.serverPort.redo();
  }
}

function staffingCommandName(intent: PositionStaffingIntent): string {
  if (intent.kind === 'fill_role') return 'Fill Role';
  if (intent.kind === 'assign') return 'Assign Person';
  if (intent.kind === 'move') return 'Move Person';
  if (intent.kind === 'unassign') return 'Unassign Person';
  if (intent.kind === 'transition') return 'Transition Seat';
  return 'Change Assignment Type';
}

function staffingCommandDescription(intent: PositionStaffingIntent): string {
  if (intent.kind === 'fill_role') return 'Add a reviewed role assignment';
  if (intent.kind === 'assign') return `Assign ${intent.personId} to ${intent.targetPositionId}`;
  if (intent.kind === 'move') return `Move ${intent.sourceAssignmentId} to ${intent.targetPositionId}`;
  if (intent.kind === 'unassign') return `Unassign ${intent.sourceAssignmentId}`;
  if (intent.kind === 'transition') {
    return `Transition ${intent.vacatingAssignmentId} to ${intent.successorPersonId}`;
  }
  return `Change ${intent.sourceAssignmentId} to ${intent.assignmentType}`;
}
